import startCase from 'lodash/startCase';
import { validate } from 'uuid';

import { isNilOrEmpty } from './objectUtils';

/**
 * Formats the current pathname into a title to use for the page.
 *
 * Example:
 * /components/pickers/date -> Date Pickers
 * /getting-started/installation -> Installation
 * /components/data-tables -> Data Tables
 *
 * @param {String} pathname - The current pathname to use
 * @return {String} the current page title for the pathname
 */
export function toPageTitle(pathname: string) {
  const path = pathname.replace('/', '').replace(/\?.*/, '');
  if (!path) {
    return '';
  } else if (path.match(/routing-examples/)) {
    return 'Routing Examples';
  }

  const [id, section] = path.split('/').reverse();
  if (path.match(/upgrade-guides/)) {
    return `Upgrade to ${id}`;
  } else if (section && section.match(/pickers|progress/)) {
    return startCase(`${id}-${section}`);
  }

  return startCase(id);
}

/**
 * Formats a string into a caterpillar-cased-string. This is really mostly helpful
 * for creating unique ids or class names.
 *
 * Example:
 * 'Hello World Stuff' -> 'hello-world-stuff'
 * 'WowzaNoWay' -> 'wowza-no-way'
 *
 * @param {String} str - the string to caterpillar-case
 * @return {String} the caterpillar-cased string.
 */
export function toCaterpillarCase(str: string) {
  return str
    .replace(/\/|\\|\[|]/g, '')
    .split(/\s|(?=[A-Z])/)
    .join('-')
    .toLowerCase();
}

/**
 * Formats a string into a concat-lower-cased-string. This is really mostly helpful
 * for creating unique ids or class names.
 *
 * Example:
 * 'Hello World Stuff' -> 'helloworldstuff'
 * 'WowzaNoWay' -> 'wowzanoway'
 *
 * @param {String} str - the string to lowercase
 * @return {String} the lowercased string.
 */
export function toConcatLowerCase(str: string) {
  return str
    .replace(/\/|\\|\[|]/g, '')
    .split(/\s|(?=[A-Z])/)
    .join('')
    .toLowerCase();
}

/**
 * Formats a string to capitalize its first letter and lower the other letters.
 *
 * Example:
 * 'hello world' -> 'Hello world'
 * 'title' -> 'Title'
 *
 * @param {String} str - the string to capitalize the first letter
 * @return {String} the formatted string.
 */
export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function underscore(s: string) {
  return s.replace('-', '_');
}

export function hyphenate(s: string) {
  return s.replace('_', '-');
}

export const truncateAndEllipsisAfter = (str: string, length: number = 100, ending: string = '...') => {
  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending;
  } else {
    return str;
  }
};

export const collapseWhitespace = (str: string) => str.replace(/\s+/g, ' ').trim();

/**
 * Remove accents from string
 * @param str the string that needs accents to be removed
 * @returns the string without accents
 */
export const normalizeString = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Removes the first character of a string.
export const removeFirstCharacter = (str: string) => {
  return str.substring(1);
};

export const countOccurrences = (str: string, searchPattern: string): number => str.split(searchPattern).length - 1;

export const isTwelveDigits = (str: string) => /^\d{12}$/.test(str);

export const alphanumericCompare = (a?: string, b?: string): number =>
  (a ?? '').localeCompare(b ?? '', undefined, { numeric: true });

export const trimToNull = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return isNilOrEmpty(trimmed) ? null : trimmed;
};

export const isUuid = (value: string): boolean => validate(value);
